import _ from 'lodash';
import { RESET_STUDIO_ACTION } from '../../consts';
import type { InvalidField, StudioFieldsValidationActions, ValidatedField } from '../../../actions/studio/fields/types';
import { fieldPathToString } from '../../../modules/studio/utils-ts';
import type { FieldPath } from '../../../types/Field';
import type { State } from './types';
import { ValidationState } from './types';

export const getDefaultState = (): State => ({
    data: {},
    state: ValidationState.Idle,
    initiator: null,
    linkedPaths: []
});

const resetStateWithValidateFields = (fields: ValidatedField[], currentState: State['data']) => {
    // cleanup validated data from state
    // cleanup validated objects
    const objectIdsIds = fields.filter((field) => !field.fieldPath.length).map((field) => field.srcObjectId) as string[];
    const newState = _.omitBy(currentState, (obj) => objectIdsIds.includes(obj.srcObjectId));
    // cleanup validated fields
    const fullFieldPaths = fields.map((field) => fieldPathToString([field.srcObjectId, ...field.fieldPath])) as string[];
    return _.omit(newState, fullFieldPaths);
};

const studioContentValidationReducer = (state = getDefaultState(), action: StudioFieldsValidationActions | null): State => {
    switch (action?.type) {
        case 'VALIDATE_FIELDS_FAILED': {
            return {
                ...state,
                data: {},
                state: ValidationState.Rejected
            };
        }

        case 'VALIDATE_FIELDS_STARTED': {
            const { fields, resetValidationData } = action.data;

            if (!fields) {
                return {
                    ...state,
                    ...(resetValidationData ? { data: {} } : {}),
                    state: ValidationState.Pending
                };
            }

            const newState = resetValidationData ? resetStateWithValidateFields(fields, state.data) : state.data;
            const result = fields.reduce(
                (
                    acc: {
                        [fieldPathStr: string]: { srcType: string; srcObjectId: string; srcProjectId: string; isValidating: boolean; fieldPath: FieldPath };
                    },
                    field
                ) => {
                    const key = fieldPathToString([field.srcObjectId, ...field.fieldPath]);
                    acc[key] = {
                        srcType: field.srcType,
                        srcObjectId: field.srcObjectId,
                        srcProjectId: field.srcProjectId,
                        fieldPath: field.fieldPath,
                        isValidating: true
                    };
                    return acc;
                },
                {}
            );

            return {
                ...state,
                data: {
                    ...(resetValidationData ? {} : newState),
                    ...result
                },
                state: ValidationState.Pending
            };
        }

        case 'VALIDATE_FIELDS_SUCCEEDED': {
            const data = action.data as ValidatedField[] | null;

            if (!data) {
                return {
                    ...state,
                    state: ValidationState.Resolved
                };
            }

            const newState = resetStateWithValidateFields(data, state.data);
            // populate state with invalid data
            const isInValid = data.some((field: ValidatedField) => !field.isValid);
            if (isInValid) {
                const invalidData = data.filter((field: ValidatedField) => !field.isValid) as InvalidField[];
                const result = invalidData.reduce((acc: State['data'], field: InvalidField) => {
                    const key = fieldPathToString([field.srcObjectId, ...field.fieldPath]);
                    acc[key] = {
                        srcType: field.srcType,
                        srcObjectId: field.srcObjectId,
                        srcProjectId: field.srcProjectId,
                        fieldPath: field.fieldPath,
                        message: field.message ?? '',
                        revalidationNeeded: field.revalidationNeeded,
                        isValidating: false
                    };
                    return acc;
                }, {});

                return {
                    ...state,
                    data: {
                        ...newState,
                        ...result
                    },
                    state: ValidationState.Resolved
                };
            }

            return {
                ...state,
                data: {
                    ...newState
                },
                state: ValidationState.Resolved
            };
        }

        case 'SET_VALIDATE_FIELDS_INITIATOR': {
            const data = action.data || {};
            return {
                ...state,
                ...data
            };
        }

        case 'SET_LINKED_INVALID_PATHS': {
            const { linkedPaths } = action.data || {};
            return {
                ...state,
                linkedPaths
            };
        }

        case RESET_STUDIO_ACTION: {
            return getDefaultState();
        }

        default: {
            return state;
        }
    }
};

export default studioContentValidationReducer;

export const localStorePaths = [];
